"use client";

import { useEffect, useState } from "react";

import { ArrowLeft } from "lucide-react";

import { usePathname, useRouter } from "next/navigation";

import styles from "./Header.module.css";

function isLegacyBaseDetailPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 3;
}

function isBaseDetailPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  return (
    (segments.length === 2 && segments[0] === "bases") ||
    isLegacyBaseDetailPath(pathname)
  );
}

function isLegalDetailPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 2 && segments[0] === "legal";
}

function isNewsDetailPath(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  return segments.length === 2 && segments[0] === "news";
}

function supportsMobileBack(pathname: string) {
  return (
    isBaseDetailPath(pathname) ||
    isLegalDetailPath(pathname) ||
    isNewsDetailPath(pathname)
  );
}

function useIsTabletUp() {
  const [isTabletUp, setIsTabletUp] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsTabletUp(media.matches);

    update();
    media.addEventListener("change", update);

    return () => media.removeEventListener("change", update);
  }, []);

  return isTabletUp;
}

export default function HeaderMobileBackButton() {
  const router = useRouter();
  const pathname = usePathname();
  const isTabletUp = useIsTabletUp();

  const [cameFromInternalRoute, setCameFromInternalRoute] = useState(false);
  const [previousPath, setPreviousPath] = useState<string | null>(null);

  useEffect(() => {
    if (!supportsMobileBack(pathname)) {
      setPreviousPath(pathname);
      return;
    }

    if (previousPath && previousPath !== pathname) {
      setCameFromInternalRoute(true);
      setPreviousPath(pathname);
      return;
    }

    if (!previousPath && typeof document !== "undefined") {
      const referrer = document.referrer;

      if (referrer) {
        try {
          const referrerUrl = new URL(referrer);
          const isInternalReferrer =
            referrerUrl.origin === window.location.origin;
          const referrerPath = `${referrerUrl.pathname}${referrerUrl.search}`;

          if (isInternalReferrer && referrerPath !== pathname) {
            setCameFromInternalRoute(true);
          }
        } catch {
          // Ignore invalid referrer URLs.
        }
      }
    }

    setPreviousPath(pathname);
  }, [pathname, previousPath]);

  useEffect(() => {
    if (!supportsMobileBack(pathname)) {
      setCameFromInternalRoute(false);
    }
  }, [pathname]);

  const showMobileBack =
    !isTabletUp && supportsMobileBack(pathname) && cameFromInternalRoute;

  useEffect(() => {
    const header = document.querySelector<HTMLElement>("[data-site-header]");
    if (!header) return;

    header.setAttribute(
      "data-has-mobile-back",
      showMobileBack ? "true" : "false"
    );

    return () => {
      header.setAttribute("data-has-mobile-back", "false");
    };
  }, [showMobileBack]);

  if (!showMobileBack) return null;

  return (
    <div className={styles.mobileBackOverlay}>
      <button
        type="button"
        className={styles.backBtn}
        onClick={() => {
          if (window.history.length > 1) {
            router.back();
            return;
          }

          router.push("/", { scroll: true });
        }}
        aria-label="Go back"
      >
        <ArrowLeft size={20} aria-hidden="true" />
      </button>
    </div>
  );
}